import { init, navigateByDirection, setFocus } from '@noriginmedia/norigin-spatial-navigation';
import * as Sentry from '@sentry/react';
import ErrorBoundaryFallback from 'common/components/error-boundary/ErrorBoundaryFallback';
import FpsMeter from 'common/components/fps-meter';
import { NoNetworkPopup } from 'common/components/no-network-popup';
import { ROUTES_WITH_HEADER, ROUTE_ROOT, ROUTE_SIGN_IN } from 'common/config/constants';
import variables from 'common/config/variables';
import { HOLD_TIMEOUT, LONG_THROTTLE_TIMEOUT } from 'common/config/variables/default';
import { usePopup } from 'common/contexts/popup';
import { AppConfigState, NavigateMode } from 'common/reducers/appConfig';
import { Errors } from 'common/reducers/errors';
import AbortHandler from 'common/services/AbortHandler';
import {
  getVirtualDebuggerMemory,
  hasCredentialsObjectInMemory,
  hasTokenInMemory
} from 'common/services/helpers';
import { getRemoteKeyName } from 'common/utils';
import GlobalStyle from 'common/utils/GlobalStyle';
import { isInputFieldFocused } from 'common/utils/helpers';
import VConsole from 'vconsole';

import {
  useMessages,
  useOnlineStatus,
  usePageNavigation,
  useQueryParams,
  useRequiredData,
  useVisibilityState
} from 'common/utils/hooks';
import NavigateModeEvent from 'common/utils/NavigateModeEvent';
import React, { Suspense, useCallback, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';
import WhiteLabel from 'white-label';
import './App.styles';
import {
  clearError,
  clearLiveChannels,
  getTranslations,
  setAuthToken,
  setVirtualDebuggerState
} from './common/actions';

init({
  debug: false,
  visualDebug: false,
  throttle: variables.KEYPRESS_TIMEOUT,
  throttleKeypresses: true
  // useGetBoundingClientRect: true
});

interface AppProps {
  errors: Errors;
  navigateMode: NavigateMode;
  clearError: VoidFunction;
  getTranslations: () => Promise<void>;
  clearLiveChannels: VoidFunction;
  setAuthToken: (token: string, refreshToken: string) => boolean | Promise<boolean>;
  setVirtualDebuggerState: (enabled: boolean) => void;
}

function App(props: AppProps) {
  const lastRefreshTime = useRef<number>(Date.now());
  const { get: getData, clear: clearData } = useRequiredData();
  const navigate = usePageNavigation();
  const { pathname } = useLocation();
  const [isOnline, prevOnlineStatus] = useOnlineStatus();
  const { openPopup, closePopup } = usePopup();
  const { handleMessages: handleCheckForMessages } = useMessages();
  const onAppVisible = useCallback(() => {
    try {
      if (Date.now() - lastRefreshTime.current >= variables.MIN_UPDATE_TIME_FOR_DATA) {
        getData();
      }
    } catch (error) {
      console.error('Error in onAppVisible:', error);
    }
  }, [getData]);
  useVisibilityState({ onVisible: onAppVisible });

  // Only show messages when we are in the main pages
  // The main pages are pages with header
  const showMessagePromptInPage = useMemo(() => ROUTES_WITH_HEADER.includes(pathname), [pathname]);

  useEffect(() => {
    if (showMessagePromptInPage) {
      handleCheckForMessages();
    }
  }, [showMessagePromptInPage, handleCheckForMessages]);

  const queryParams = useQueryParams();

  useEffect(() => {
    (async () => {
      const authToken = queryParams.get('authToken');
      const refreshToken = queryParams.get('refreshToken');
      if (authToken && refreshToken) {
        const response = await props.setAuthToken(authToken, refreshToken);
        if (response) {
          // Reload the page to ensure that the new auth token is used in all subsequent requests
          location.reload();
        }
      }
    })();
  }, [queryParams]);

  // !!! PREVENT back key
  // Needed for Phillips TV
  // This useEffect is separate from the one using the navigateMode
  // Because we want only on app start to attach this event
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Check if back key
      if (getRemoteKeyName(e.keyCode) === 'BACK' && !isInputFieldFocused()) {
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  useEffect(() => {
    let delta = 0;
    let timeout: NodeJS.Timeout;
    // Check if virtual debugger is enabled in memory and set the state accordingly
    const virtualDebuggerEnabledInMemory = getVirtualDebuggerMemory();
    if (virtualDebuggerEnabledInMemory) {
      props.setVirtualDebuggerState(true);
      window.vConsole = new VConsole();
      // If virtual console was previously enabled by default show the console
      window.vConsole.show();
      //
    }
    const onKeyDown = (e: KeyboardEvent) => {
      // Check for debug key
      if (getRemoteKeyName(e.keyCode) === 'DEBUG' && !e.repeat) {
        delta = Date.now();
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      // Check for debug key
      if (getRemoteKeyName(e.keyCode) === 'DEBUG') {
        if (Date.now() - delta > HOLD_TIMEOUT) {
          console.info('[DEBUG] enabling debug mode');
          if (!window.vConsole) {
            props.setVirtualDebuggerState(true);
            window.vConsole = new VConsole();
          } else {
            timeout = setTimeout(() => {
              window.vConsole && window.vConsole.destroy();
              window.vConsole = null;
              props.setVirtualDebuggerState(false);
              const remoteJsScript = document.getElementById('remote-js-script');
              if (remoteJsScript) {
                remoteJsScript.remove();
              }
            }, LONG_THROTTLE_TIMEOUT);
          }
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      timeout && clearTimeout(timeout);
      window.vConsole && window.vConsole.destroy();
    };
  }, []);

  useEffect(() => {
    const onScroll = (event: any) => {
      props.navigateMode !== 'DIRECTIONAL' && NavigateModeEvent.setMode('DIRECTIONAL');
      if (event.deltaY < 0) {
        navigateByDirection('up', event);
      } else {
        // wheeled down
        navigateByDirection('down', event);
      }
    };

    const cursorStateChange = (event: any) => {
      NavigateModeEvent.setMode(
        event.detail && event.detail.visibility ? 'POINTER' : 'DIRECTIONAL'
      );
    };

    // Consider mouse move as pointer mode
    // We cannot rely on cursorStateChange because some platforms do not support it
    let cursorMoveTimeout: NodeJS.Timeout;
    const onMouseMove = () => {
      clearTimeout(cursorMoveTimeout);
      props.navigateMode !== 'POINTER' && NavigateModeEvent.setMode('POINTER');
      cursorMoveTimeout = setTimeout(() => {
        NavigateModeEvent.setMode('DIRECTIONAL');
      }, variables.MOUSE_MOVE_TIMEOUT);
    };

    window.addEventListener('wheel', onScroll);
    document.addEventListener('cursorStateChange', cursorStateChange);
    window.addEventListener('mousemove', onMouseMove);

    return () => {
      document.removeEventListener('cursorStateChange', cursorStateChange);
      window.removeEventListener('wheel', onScroll);
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, [props.navigateMode]);

  useEffect(() => {
    if (!isOnline) {
      // Clear data when network is missing
      clearData();
      //
      openPopup({
        id: 'no-network',
        content: <NoNetworkPopup />
      });
    } else if (!prevOnlineStatus) {
      // Wait after network restore to ensure stable network connection
      // Refresh with new data
      getData().then((response) => {
        //
        closePopup('no-network');
        // Set focus
        setFocus('APP_CONTAINER');
        //
        if (!response) {
          console.error('Failed to get data after network restore');
          navigate(ROUTE_SIGN_IN, true, true);
        }
      });
    }
  }, [isOnline, prevOnlineStatus]);

  useEffect(() => {
    if (props.errors.redirect) {
      navigate(props.errors.redirect, true, true);
    }
  }, [props.errors]);

  useEffect(() => {
    (async () => {
      // Always load translations
      await props.getTranslations();
      //
      if (hasTokenInMemory() || hasCredentialsObjectInMemory()) {
        navigate(ROUTE_ROOT, true, true);
        return;
      } else {
        navigate(ROUTE_SIGN_IN);
        return;
      }
    })();
    return () => {
      AbortHandler.abort();
    };
  }, []);

  return (
    <>
      <Suspense>
        {GlobalStyle.render()}
        <Sentry.ErrorBoundary
          fallback={((data) => <ErrorBoundaryFallback {...data} />) as Sentry.FallbackRender}
        >
          <WhiteLabel />
          <FpsMeter />
        </Sentry.ErrorBoundary>
      </Suspense>
    </>
  );
}

const mapStateToProps = ({ errors, appConfig }: { errors: any; appConfig: AppConfigState }) => ({
  errors,
  navigateMode: appConfig.navigateMode
});

export default React.memo(
  connect(mapStateToProps, {
    clearError,
    getTranslations,
    clearLiveChannels,
    setAuthToken,
    setVirtualDebuggerState
  })(App)
);
